import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Carousel } from 'react-bootstrap';
import Globe from 'react-globe.gl';
import './HeroCarousel.css';

const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const globeEl = useRef();

  useEffect(() => {
    if (globeEl.current && index === 1) {
      // Auto-rotate globe
      globeEl.current.controls().autoRotate = true;
      globeEl.current.controls().autoRotateSpeed = 0.5;
      globeEl.current.controls().enableZoom = false;
      
      // Set initial point of view
      globeEl.current.pointOfView({ lat: 20, lng: 0, altitude: 2.5 }, 0);
    }
  }, [index]);

  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  const slides = [
    {
      id: 1,
      title: 'CONNECT YOUR LOGISTICS BUSINESS TO THE WORLD',
      description: 'Collaborate with a global community of freight forwarders, create strong partnerships, and unlock new opportunities across international supply chains.',
      videoUrl: 'https://videos.pexels.com/video-files/6894128/6894128-uhd_2560_1440_25fps.mp4',
      fallbackImage: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1920&q=80',
      posterImage: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1920&q=80',
      ctaText: 'EXPLORE MEMBERSHIP',
      ctaLink: "/join-us"
    },
    {
      id: 2,
      title: 'EXPAND YOUR GLOBAL LOGISTICS NETWORK',
      description: 'Connect with reliable freight forwarding partners and gain access to new markets, shipping lanes, and international opportunities.',
      videoUrl: 'https://videos.pexels.com/video-files/4439425/4439425-uhd_2560_1440_25fps.mp4',
      fallbackImage: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80',
      posterImage: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80',
      ctaText: 'LEARN MORE',
      ctaLink: '/benefits'
    },
    {
      id: 3,
      title: 'FINANCIAL SECURITY FOR GLOBAL LOGISTICS',
      description: 'Protect your transactions and streamline operations by partnering with verified logistics professionals committed to reliability, transparency, and industry standards.',
      videoUrl: 'https://videos.pexels.com/video-files/3129671/3129671-uhd_2560_1440_30fps.mp4',
      fallbackImage: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&q=80',
      posterImage: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&q=80',
      ctaText: 'JOIN EFFN',
      ctaLink: "/join-us"
    }
  ];

  return (
    <div className="hero-carousel-wrapper">
      <Carousel 
        activeIndex={index} 
        onSelect={handleSelect} 
        interval={4000}
        fade={false}
        controls={true}
        indicators={true}
        pause={false}
      >
        {slides.map((slide, idx) => (
          <Carousel.Item key={slide.id}>
            <div className="hero-slide-centered">
              {/* Background Video/Image */}
              <div className="video-background">
                {slide.videoUrl ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="hero-video"
                    key={`video-${slide.id}`}
                    poster={slide.posterImage}
                  >
                    <source src={slide.videoUrl} type="video/mp4" />
                    {/* Fallback to image if video fails */}
                    {slide.fallbackImage && (
                      <img 
                        src={slide.fallbackImage} 
                        alt={slide.title}
                        className="hero-video"
                      />
                    )}
                  </video>
                ) : slide.backgroundImage ? (
                  <img 
                    src={slide.backgroundImage} 
                    alt={slide.title}
                    className="hero-video"
                  />
                ) : null}
                <div className="video-overlay-dark"></div>
              </div>

              {/* Left-Right Layout with World Map */}
              <div className="container hero-container-split">
                <div className="row align-items-center">
                  {/* Left Content */}
                  <div className="col-lg-6">
                    <div className="hero-content-left" key={`content-${idx}`}>
                      <h1 className="hero-title-left animate-fade-in">
                        {slide.title}
                      </h1>
                      
                      <p className="hero-description-left animate-fade-in-delay">
                        {slide.description}
                      </p>

                      <div className="hero-cta-left animate-fade-in-delay-2">
                        <Link to={slide.ctaLink} className="btn-hero-left">
                          {slide.ctaText}
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Right World Map or Globe */}
                  <div className="col-lg-6">
                    {/* World Map SVG for all slides */}
                    <div className="hero-world-map">
                        <svg viewBox="0 0 800 400" xmlns="http://www.w3.org/2000/svg" className="world-map-svg">
                          <defs>
                            <linearGradient id="mapGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style={{stopColor: '#a8d5ff', stopOpacity: 0.3}} />
                              <stop offset="50%" style={{stopColor: '#ffffff', stopOpacity: 0.4}} />
                              <stop offset="100%" style={{stopColor: '#a8d5ff', stopOpacity: 0.3}} />
                            </linearGradient>
                            <filter id="glow">
                              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                              <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                              </feMerge>
                            </filter>
                          </defs>
                          {/* Simplified World Map Paths */}
                          <g fill="url(#mapGradient)" stroke="rgba(255,255,255,0.5)" strokeWidth="0.5" filter="url(#glow)">
                            {/* North America */}
                            <path d="M150,80 L180,70 L200,75 L220,65 L240,70 L250,85 L245,100 L235,110 L220,115 L200,120 L180,115 L165,105 L155,95 Z"/>
                            {/* South America */}
                            <path d="M220,180 L235,170 L245,175 L250,190 L248,210 L240,230 L230,240 L220,235 L215,220 L218,200 Z"/>
                            {/* Europe */}
                            <path d="M380,70 L400,65 L420,70 L430,80 L425,95 L410,100 L395,95 L385,85 Z"/>
                            {/* Africa */}
                            <path d="M380,120 L400,115 L420,120 L435,135 L440,160 L435,185 L420,200 L400,205 L385,200 L375,180 L375,145 Z"/>
                            {/* Asia */}
                            <path d="M450,60 L480,55 L520,60 L560,70 L590,85 L600,100 L595,120 L580,130 L560,135 L540,130 L520,120 L500,110 L480,100 L465,85 Z"/>
                            {/* Australia */}
                            <path d="M580,220 L610,215 L635,225 L640,245 L630,260 L610,265 L590,260 L580,245 Z"/>
                            {/* Connection Lines */}
                            <line x1="250" y1="100" x2="380" y2="90" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="5,5"/>
                            <line x1="430" y1="85" x2="480" y2="80" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="5,5"/>
                            <line x1="420" y1="150" x2="480" y2="120" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="5,5"/>
                            <line x1="560" y1="130" x2="590" y2="220" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="5,5"/>
                          </g>
                          {/* Connection Dots */}
                          <g fill="rgba(255,255,255,0.6)">
                            <circle cx="200" cy="100" r="3"/>
                            <circle cx="240" cy="200" r="3"/>
                            <circle cx="410" cy="85" r="3"/>
                            <circle cx="410" cy="160" r="3"/>
                            <circle cx="520" cy="90" r="3"/>
                            <circle cx="610" cy="240" r="3"/>
                          </g>
                        </svg>
                      </div>
                  </div>
                </div>
              </div>
            </div>
          </Carousel.Item>
        ))}
      </Carousel>
    </div>
  );
};

export default HeroCarousel;
